import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import Tooltip from '../common/Tooltip';
import AssistantChat from '../assistant/AssistantChat';
import { AssistantConversation } from '../../types';
import apiService from '../../services/api';
import { ASSISTANT_NAME } from '../../constants/branding';

const CONTEXT_BADGES: Record<string, { label: string; className: string }> = {
  general: { label: 'General', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  project: { label: 'Project', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  orchestration: { label: 'Workflow', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  agent: { label: 'Agent', className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const BootcampAssistant: React.FC = () => {
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [conversations, setConversations] = useState<AssistantConversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState<AssistantConversation | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      setIsLoadingConversations(true);
      const data = await apiService.getAssistantConversations();
      setConversations(data.slice(0, 20));
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleResumeConversation = (conversation: AssistantConversation) => {
    setSelectedConversation(conversation);
    setIsChatOpen(true);
  };

  const handleNewChat = () => {
    setSelectedConversation(null);
    setIsChatOpen(true);
  };

  const handleBackFromChat = () => {
    setIsChatOpen(false);
    setSelectedConversation(null);
    loadConversations();
  };

  const handleDeleteConversation = async (e: React.MouseEvent, conversationId: number) => {
    e.stopPropagation();
    try {
      await apiService.deleteAssistantConversation(conversationId);
      setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  if (isChatOpen) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={handleBackFromChat}
            className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-warm-white transition-colors mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Back
          </button>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-warm-white">
            {selectedConversation ? selectedConversation.title : 'New Conversation'}
          </h1>
        </div>
        <div className="flex-1 overflow-hidden max-w-3xl w-full mx-auto">
          <AssistantChat
            key={selectedConversation?.conversation_id ?? 'new'}
            initialConversationId={selectedConversation?.conversation_id}
            contextType={selectedConversation?.context_type}
            projectId={selectedConversation?.project_id}
            contextId={selectedConversation?.context_id}
            onClose={handleBackFromChat}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 overflow-auto h-full">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/bootcamp')}
          className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-warm-white transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Bootcamp
        </button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-warm-white mb-2">
              {ASSISTANT_NAME}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Chat with {ASSISTANT_NAME} for help with workflows, agents, and schemas.
            </p>
          </div>
          <button
            onClick={handleNewChat}
            className="flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Conversation
          </button>
        </div>

        {/* Recent Conversations */}
        <div className="bg-white dark:bg-cap-dark p-6 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white mb-4">Recent Conversations</h2>

          {isLoadingConversations ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
              <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading conversations...</span>
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p>No conversations yet</p>
              <p className="text-sm mt-1">Start a new conversation to get AI-powered assistance</p>
              <button
                onClick={handleNewChat}
                className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
              >
                Start a Conversation
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {conversations.map((conversation) => {
                const badge = CONTEXT_BADGES[conversation.context_type] || CONTEXT_BADGES.general;
                return (
                  <button
                    key={conversation.conversation_id}
                    onClick={() => handleResumeConversation(conversation)}
                    className="w-full flex items-center justify-between py-3 px-3 -mx-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left group"
                  >
                    <div className="flex-1 min-w-0 mr-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-warm-white truncate">
                          {conversation.title}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${badge.className}`}>
                          {badge.label}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {conversation.updated_at
                          ? formatRelativeTime(conversation.updated_at)
                          : conversation.created_at
                          ? formatRelativeTime(conversation.created_at)
                          : ''}
                      </span>
                    </div>
                    <Tooltip content="Delete conversation">
                    <button
                      onClick={(e) => handleDeleteConversation(e, conversation.conversation_id)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    </Tooltip>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BootcampAssistant;
